/**
 * The Walliyo console tab: the same table over the staff catalogue.
 *
 * Two records rather than three — everyone, or one employee — because a
 * console feature belongs to staff. Source is the interesting column here: it
 * says whether the answer came from a role, from the floor under all staff,
 * or from an override set on this person, which is the only one this screen
 * can take away.
 */

import type { StaffFeatureListItem } from "@wallyo/contracts/console/configuration";

import { categoryName, featureName, t } from "../../i18n";
import type { Draft } from "./feature-save-model";
import {
  isOverride,
  isStaffChanged,
  shownStaffValue,
  staffNotEditableLabel,
  staffSourceLabel,
  type StaffSubjectFields,
} from "./staff-feature-model";

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/** Who the table is about, always: an employee, all staff, or neither. */
function StaffPill(props: {
  resolved: { uuid: number; name: string; roles: readonly string[] } | null;
  missing: "none" | "unknown" | "customer";
}) {
  const { resolved, missing } = props;
  if (missing !== "none") {
    return (
      <span className="subject-pill missing" role="status">
        <span className="subject-name">
          {t(missing === "customer" ? "features.subject.isCustomer" : "features.pill.unknownStaff")}
        </span>
      </span>
    );
  }
  if (resolved) {
    return (
      <span className="subject-pill">
        <span className="subject-initials" aria-hidden="true">
          {initialsOf(resolved.name)}
        </span>
        <span className="subject-name">{resolved.name}</span>
        <span className="subject-sep" aria-hidden="true" />
        <span className="subject-kind-tag">{resolved.roles.join(", ") || t("features.staffSource.byRole")}</span>
      </span>
    );
  }
  // Nobody searched yet: the pill has nothing to say, so it says nothing.
  return null;
}

export function StaffSubjectBar(props: {
  fields: StaffSubjectFields;
  resolved: { uuid: number; name: string; roles: readonly string[] } | null;
  missing: "none" | "unknown" | "customer";
  problem: string | null;
  searching: boolean;
  onChange: (next: Partial<StaffSubjectFields>) => void;
  onSearch: () => void;
}) {
  const { fields, resolved, missing, problem, searching, onChange, onSearch } = props;
  return (
    <form
      className="card subject-bar"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSearch();
      }}
    >
      {/* One box, no radios: this screen is about one employee. What all staff
          can do is the role grid's question, in Rôles & équipe. */}
      <label className="subject-uuid">
        <span className="subject-label">{t("features.subject.employeeUuid")}</span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          value={fields.uuid}
          placeholder={t("features.subject.staffUuidHint")}
          onChange={(event) => onChange({ uuid: event.target.value })}
        />
      </label>
      <button type="submit" className="btn primary" disabled={searching}>
        {searching ? t("features.subject.searching") : t("features.subject.search")}
      </button>
      {problem ? <span className="subject-problem">{problem}</span> : null}
      <span className="subject-who">
        <StaffPill resolved={resolved} missing={missing} />
      </span>
    </form>
  );
}

export function StaffFeatureTable(props: {
  features: readonly StaffFeatureListItem[];
  draft: Draft;
  onToggle: (feature: StaffFeatureListItem) => void;
}) {
  const { features, draft, onToggle } = props;
  return (
    <div className="card">
      <table className="config-list feature-table">
        <thead>
          <tr>
            <th>{t("features.col.feature")}</th>
            <th>{t("features.col.category")}</th>
            <th>{t("features.col.source")}</th>
            <th>{t("features.col.value")}</th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => {
            const on = shownStaffValue(feature, draft);
            const locked = staffNotEditableLabel(feature);
            const changed = isStaffChanged(feature, draft);
            const cleared = draft[feature.settingId] === null;
            return (
              <tr key={feature.settingId} className={changed ? "dirty" : undefined}>
                <td>{featureName(feature.settingId)}</td>
                <td>{categoryName(feature.category)}</td>
                <td className="source-cell">
                  <span className={isOverride(feature) ? "pill own" : "muted"}>{staffSourceLabel(feature)}</span>
                </td>
                <td>
                  <span className="value-cell">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-label={featureName(feature.settingId)}
                      className={`toggle${on ? " on" : ""}${changed ? " dirty" : ""}`}
                      disabled={locked !== null}
                      onClick={() => onToggle(feature)}
                    >
                      <span className="knob" />
                    </button>
                    <span className="value-word">{t(on ? "features.value.on" : "features.value.off")}</span>
                    {changed && !cleared ? <span className="value-note unsaved">{t("features.unsaved")}</span> : null}
                    {cleared ? <span className="value-note cleared">{t("features.cleared")}</span> : null}
                    {locked ? <span className="value-note locked">{locked}</span> : null}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
